/* @flow */
import { sumBy } from 'lodash';
import PropTypes from 'prop-types';
import * as React from 'react';

import LinkConstants from 'constants/LinkConstants';
import OrganizationProfile from 'entities/OrganizationProfile';
import remindWwwRequest from 'lib/remindWwwRequest';
import { SchoolView } from 'schools/components/SchoolView';
import type { EngagementRating } from 'schools/constants/EngagementRatings';

type Props = {
  params: { organizationId: string },
};

export type School = {
  city: string,
  great_schools_url?: string,
  messaging_users_count: number,
  next_rating: EngagementRating,
  org_name: string,
  pct_schools_engaged_rating: number,
  pct_schools_next_rating: number,
  pct_schools_same_rating: number,
  pct_similar_schools_active: number,
  pct_similar_schools_emerging: number,
  pct_similar_schools_growing: number,
  school_pages_equivalent_grade: 'high_school' | 'not_high_school',
  school_pages_rating: EngagementRating,
  state: string,
  url: string,
};

type CommunicationStats = {
  announcements: number,
  conversations: number,
  date: string,
};

type ReachStats = {
  date: string,
  parents: number,
  students: number,
  teachers: number,
};

export type Stats = {
  communication_usage: {
    chart: Array<CommunicationStats>,
  },
  reach_usage: {
    chart: Array<ReachStats>,
  },
};

type State = {
  school: ?School,
  stats: ?Stats,
};

export default class SchoolViewDataContainer extends React.Component<Props, State> {
  // eslint-disable-next-line @remind/remind/legacy-context
  static contextTypes = {
    // eslint-disable-next-line react/forbid-prop-types
    router: PropTypes.object.isRequired,
  };

  state: State = {
    school: null,
    stats: null,
  };

  componentDidMount() {
    this.loadOrg(this.props.params.organizationId);
  }

  getProfile = (orgUuid: string) => {
    const onSuccess = response => {
      if (!response) {
        this.redirect();
      }

      this.setState({ school: response });
    };

    const onError = () => {
      this.redirect();
    };

    return remindWwwRequest(`/school_pages/${orgUuid}/profile`).then(onSuccess, onError);
  };

  getStats = (orgUuid: string) => {
    const onSuccess = response => {
      if (!response) {
        return;
      }

      const communicationSum = sumBy(
        response.communication_usage.chart,
        item => item.announcements + item.conversations
      );
      const reachSum = sumBy(response.reach_usage.chart, item => item.parents + item.students);

      if (communicationSum === 0 || reachSum === 0) {
        return;
      }

      this.setState({ stats: response });
    };

    return remindWwwRequest(`/school_pages/${orgUuid}/usage`).then(onSuccess);
  };

  // `organizationId` can be the org id or org slug
  loadOrg = (organizationId: number | string) => {
    OrganizationProfile.fetch({ id: organizationId }).then(
      organizationProfile => {
        const orgUuid = ((organizationProfile: any): OrganizationProfile).getUuid();

        this.getProfile(orgUuid);
        this.getStats(orgUuid);
      },
      () => {
        this.redirect();
      }
    );
  };

  redirect = () => {
    window.location = LinkConstants.PLANS;
  };

  render() {
    const { school, stats } = this.state;

    if (!school) {
      return null;
    }

    return <SchoolView school={school} stats={stats} />;
  }
}
